import React, { createContext, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { logger } from '../utils/logger';

interface AuthContextType {
  user: any;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  isAdmin: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, metadata?: any) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const isAdminDomain = window.location.hostname.startsWith('admin.');

  useEffect(() => {
    const checkAdminStatus = async (userId: string) => {
      try {
        if (!userId) return false;

        const { data: userData, error } = await supabase
          .from('users')
          .select('is_admin')
          .eq('id', userId)
          .maybeSingle(); // Changed from single() to maybeSingle()

        if (error) {
          logger.error('Error checking admin status:', error);
          return false;
        }

        return userData?.is_admin || false;
      } catch (err) {
        logger.error('Error in checkAdminStatus:', err);
        return false;
      }
    };

    setIsLoading(false);
    const mockUser = { id: '00000000-0000-0000-0000-000000000000', name: 'Guest User', email: 'guest@example.com' };
    setUser(mockUser);
    setIsAuthenticated(true);
    
    if (isAdminDomain) {
      setIsAdmin(true);
    } else {
      checkAdminStatus(mockUser.id).then(setIsAdmin);
    }
  }, []);

  const signIn = async (email: string, password: string) => {
    setUser({ id: '00000000-0000-0000-0000-000000000000', name: 'Guest User', email: email });
    setIsAuthenticated(true);
    navigate('/dashboard');
  };

  const signUp = async (email: string, password: string, metadata?: any) => {
    setUser({ id: '00000000-0000-0000-0000-000000000000', name: metadata?.name || 'Guest User', email: email });
    setIsAuthenticated(true);
    navigate('/dashboard');
  };

  const signOut = async () => {
    navigate('/', { replace: true });
  };

  const [isAuthenticated, setIsAuthenticated] = useState(true);

  const value = {
    user,
    isAuthenticated,
    isLoading,
    error,
    isAdmin,
    signIn,
    signUp,
    signOut
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}